import { useEffect } from 'react';
import { Routes } from '.generated/templates/Project.Routes.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import {
  DictionaryPhrases,
  RouteData,
  Text,
  Field,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { LayoutPageSectionShell } from 'components/authorable/layout/LayoutPageSection/LayoutPageSection';
import AddToCart from './Components/AddToCart';
import ProductImage from './Components/ProductImage';
import RelatedProducts from './Components/RelatedProducts';
import { LayoutLeftThirdShell } from 'components/authorable/layout/LayoutLeftThird/LayoutLeftThird';
import HeadingTag from 'components/helpers/HeadingTag';
import classnames from 'classnames';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import Button from 'components/helpers/Button';
import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';
import useDictionary from 'lib/sitecore/use-dictionary';
import OtherProducts from './Components/OtherProducts';
import {
  EcommerceListDataLayerObject,
  EcommerceDataLayerObject,
  gtmEcommerceDatalayerPush,
} from 'lib/util/gtm-datalayer';

export type ProductDetailPageProps = RouteData &
  Routes.Fields.ProductDetailsPage & {
    fields: {
      relatedProduct?: (Sitecore.Override.ItemEx &
        Routes.Fields.ProductDetailsPage & {
          fields: {
            relatedProductImageUrl?: string;
          };
        })[];
    };
  };

const usePageLoadGtmPushEffect = ({ route }: { route: ProductDetailPageProps }) => {
  useEffect(() => {
    const { fields, itemId } = route;
    const { relatedProduct } = fields;

    gtmEcommerceDatalayerPush({
      event: 'view_item',
      ecommerce: {
        value: '0',
        currency: 'USD',
        items: [
          {
            item_id: fields?.productNumber.value ?? itemId,
            item_name: fields?.productTitle.value,
            affiliation: 'N/A',
            coupon: 'N/A',
            discount: 'N/A',
            index: '0',
            item_brand: 'NMDP',
            item_category: fields?.mediaType?.map((x) => x.displayName).toString(),
            item_category2: fields?.patientResources?.map((x) => x.displayName).toString(),
            item_category3: fields?.productLanguages?.map((x) => x.displayName).toString(),
            item_category4: fields?.resourceType?.map((x) => x.displayName).toString(),
            item_list_name: 'order materials',
            item_variant: 'N/A',
            price: 0,
            quantity: fields?.itemMin.value,
          },
        ],
      },
    } as EcommerceDataLayerObject);

    if (relatedProduct.length > 0) {
      gtmEcommerceDatalayerPush({
        event: 'view_item_list',
        ecommerce: {
          item_list_id: 'order_materials',
          item_list_name: 'order materials',
          items: relatedProduct.map((item, i) => {
            return {
              item_id: item.fields?.productNumber ?? itemId,
              item_name: item.name,
              item_list_name: 'related products',
              index: i.toString(),
            };
          }),
        },
      } as EcommerceListDataLayerObject);
    }
  }, [route]);
};

const LeftColumn = ({
  productImage,
  productTitle,
}: {
  productTitle?: Field<string>;
  productImage: Sitecore.Override.ItemEx[];
}): React.JSX.Element => {
  return (
    <>
      <HeadingTag
        headingLevel="h1"
        className={classnames('heading-products mb-[18px] block md:mb-[11px] md:hidden')}
      >
        {productTitle?.value}
      </HeadingTag>
      <ProductImage productImages={productImage} />
    </>
  );
};

const RightColumn = ({
  dictionary,
  pageUrl,
  route,
}: {
  dictionary: DictionaryPhrases;
  pageUrl: string;
  route: ProductDetailPageProps;
}): React.JSX.Element => {
  const { fields } = route;
  if (!route || !fields) {
    return <></>;
  }
  const { productImage } = fields;
  let showAddToCart = fields?.showAddToCart.value;
  if (fields.itemMaxUs == null || fields.itemMin == null || fields.itemMaxIntl == null) {
    showAddToCart = false;
  }
  const productNumber = classnames('text-black');
  const productImageUrl = productImage?.length > 0 ? productImage[0].url : '';
  const displayOtherFormat =
    fields.productPlayStoreLink.value.href !== '' ||
    fields.productAppleStoreLink.value.href !== '' ||
    fields.otherLanguageDocuments.length > 0;

  return (
    <>
      <div className={classnames('md:pl-[3em] lg:pl-[5em] xl:pl-[6.9em]')}>
        <HeadingTag
          headingLevel="h1"
          className={classnames('heading-products mb-[11px] hidden md:block')}
        >
          {fields?.productTitle.value}
        </HeadingTag>
        <div className={classnames('mb-[16px]', 'text-body', 'leading-2', 'font-medium')}>
          <p className={classnames('inline')}>
            {dictionary.ProductDetails_ItemNumber
              ? dictionary.ProductDetails_ItemNumber
              : 'Item #:'}{' '}
          </p>
          <Text
            className={classnames(productNumber, 'inline')}
            field={fields.productNumber}
            tag="p"
          />
        </div>
        <RichTextA11yWrapper
          className={classnames('product-description', 'mb-[31px]', 'text-blue-navy')}
          field={fields.productDescription}
        />
        <div className={classnames('mb-7px', 'flex', 'flex-wrap', 'justify-start')}>
          {showAddToCart && (
            <AddToCart
              itemMaxIntl={fields.itemMaxIntl.value}
              itemMin={fields.itemMin.value}
              itemMaxUs={fields.itemMaxUs.value}
              id={route.itemId}
              image={productImageUrl}
              itemNumber={fields.productNumber.value}
              quantity={fields.itemMin.value}
              price={0}
              name={fields.productTitle.value}
              productLink={pageUrl}
            />
          )}
          <div
            className={classnames(
              `${
                showAddToCart && fields.productButton?.value?.href
                  ? 'order-3 lg:order-2 lg:border-l lg:border-solid lg:border-gray-midGray lg:pl-[16px]'
                  : ''
              }`
            )}
          >
            <Button
              linkField={fields.productButton}
              variant="secondary"
              className={classnames(
                'mt-[30px] w-full text-center lg:mt-[0px] lg:w-auto lg:px-[30px]'
              )}
            />
          </div>
        </div>
        <hr className={classnames('mb-[30px] mt-[27px] text-gray-midGray')} />

        {displayOtherFormat && <OtherProducts fields={fields} />}
      </div>
    </>
  );
};

const ProductDetailPage = (): React.JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const pageUrl = sitecoreContext?.itemPath || '';
  const { fields } = sitecoreContext.route as ProductDetailPageProps;
  const relatedProducts = fields?.relatedProduct;
  const productImage = fields?.productImage;
  const backToCatalogUrl = pageUrl.substring(0, pageUrl.lastIndexOf('/'));

  usePageLoadGtmPushEffect({ route: sitecoreContext.route as ProductDetailPageProps });

  const dictionary = useDictionary();
  const leftColumn = <LeftColumn productTitle={fields?.productTitle} productImage={productImage} />;

  const rightColumn = (
    <RightColumn
      dictionary={dictionary}
      pageUrl={pageUrl}
      route={sitecoreContext.route as ProductDetailPageProps}
    />
  );
  return (
    <div data-component="authorable/pages/productdetailpage">
      <LayoutPageSectionShell theme="theme-inherit" padding="tiny">
        <div className={classnames('mb-[10px]')}>
          <Button
            linkField={{ value: { href: backToCatalogUrl, text: ' ' } }}
            variant="secondary"
            className={classnames('mb-4 border-2 py-0.5 sm:py-0.5')}
          >
            <SvgIcon
              className={classnames('mr-2')}
              icon="arrow-left"
              size="xs"
              viewBoxSize="0 0 48 48"
            />
            Back to material catalog
          </Button>
        </div>
        <LayoutLeftThirdShell left={leftColumn} right={rightColumn} isSearchPage={false} />
        <hr className={classnames('mb-[30px] mt-[27px] text-gray-midGray')} />
        <div>
          <RelatedProducts relatedProduct={relatedProducts} />
        </div>
      </LayoutPageSectionShell>
    </div>
  );
};

export default ProductDetailPage;
